import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import SchedulerService from "../../services/SchedulerService";
import NotificationTemplateService from "../../services/NotificationTemplateService";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ListAltIcon from "@mui/icons-material/ListAlt";
import RenderOnRole from "../../components/RenderOnRole";

const CHANNELS = [
  { type: "SMS", label: "SMS", color: "info" },
  { type: "EMAIL", label: "Email", color: "primary" },
  { type: "VOICE", label: "Voice", color: "warning" },
  { type: "APP_MESSAGE", label: "App Msg", color: "secondary" },
];

const TASK_TYPE_COLORS = {
  SMS: "info",
  EMAIL: "primary",
  VOICE: "warning",
  APP_MESSAGE: "secondary",
};

const TASK_TYPE_LABELS = {
  SMS: "SMS",
  EMAIL: "Email",
  VOICE: "Voice Call",
  APP_MESSAGE: "App Message",
};

const TASK_TYPE_TO_TEMPLATE_TYPE = {
  SMS: "SMS",
  EMAIL: "EMAIL",
  VOICE: "VOICE",
  APP_MESSAGE: "APP",
};

const EMPTY_DAY = { dayNumber: 1, label: "", isEnabled: true };
const EMPTY_TASK = {
  taskType: "SMS",
  hourUtc: 9,
  subject: "",
  idProcess: "",
  bodyOverride: "",
  conditionJson: "",
  sequenceOrder: 1,
  status: "ACTIVE",
};

const EASTERN_TIME_ZONE = "America/New_York";

const normalizeHour = (hour) => ((hour % 24) + 24) % 24;
const getEasternTimeInfo = (hourUtc, referenceDate = new Date()) => {
  if (!Number.isFinite(hourUtc))
    return { hour: "", timeZoneName: "", utcOffsetHours: -5 };

  const date = new Date(
    Date.UTC(
      referenceDate.getUTCFullYear(),
      referenceDate.getUTCMonth(),
      referenceDate.getUTCDate(),
      hourUtc
    )
  );
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: EASTERN_TIME_ZONE,
    hour: "numeric",
    hourCycle: "h23",
    timeZoneName: "short",
  }).formatToParts(date);
  const hour = Number(parts.find((part) => part.type === "hour").value);
  const timeZoneName = parts.find((part) => part.type === "timeZoneName").value;
  const utcOffsetHours = timeZoneName === "EDT" ? -4 : -5;

  return { hour, timeZoneName, utcOffsetHours };
};

const easternToUtcHour = (hourEastern) => {
  const { utcOffsetHours } = getEasternTimeInfo(0);
  return normalizeHour(hourEastern - utcOffsetHours);
};

export default function SchedulerTemplateDetail({
  templateIdProp,
  templateProp,
  onClose,
} = {}) {
  const { id: idFromRoute } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const templateId = templateIdProp ?? idFromRoute;
  const goBack =
    onClose ?? (() => navigate("/communication/scheduler/templates"));

  const [dayDlg, setDayDlg] = useState(null); // null | "new" | dayObject
  const [taskDlg, setTaskDlg] = useState(null); // null | { dayId, task?: obj }
  const [dayForm, setDayForm] = useState(EMPTY_DAY);
  const [taskForm, setTaskForm] = useState(EMPTY_TASK);
  const [selectedDay, setSelectedDay] = useState(null);
  const [confirmDeleteDay, setConfirmDeleteDay] = useState(null); // day object or null
  const [notifPickerOpen, setNotifPickerOpen] = useState(false);
  const [notifSearch, setNotifSearch] = useState("");
  const [createNotifDlg, setCreateNotifDlg] = useState(false);
  const [createNotifForm, setCreateNotifForm] = useState({
    idProcess: "",
    typeTemplate: "SMS",
    idTemplate: "",
    template: "",
    lang: "",
    notificationTypeId: "",
    status: "ACTIVE",
  });
  const [feedback, setFeedback] = useState({
    open: false,
    type: "success",
    message: "",
  });

  const showFeedback = (type, message) =>
    setFeedback({ open: true, type, message });

  // ── Template info (passed via navigation state, prop, or refetched) ───
  const template = templateProp ?? state?.template;

  // ── Notification templates (for process-picker) ───────────────────────
  const { data: notifTemplates } = useQuery(
    ["notification-templates-picker"],
    () => NotificationTemplateService.list(),
    { select: (r) => r.data, enabled: notifPickerOpen }
  );

  // ── Days ──────────────────────────────────────────────────────────────
  const { data: days, isLoading: daysLoading } = useQuery(
    ["scheduler-days", templateId],
    () => SchedulerService.getDays(templateId),
    { select: (r) => r.data, enabled: !!templateId }
  );

  // ── Tasks per day (object keyed by dayId) ────────────────────────────
  const { data: tasksMap } = useQuery(
    ["scheduler-tasks-bulk", days],
    async () => {
      if (!days || days.length === 0) return {};
      const results = await Promise.all(
        days.map((d) =>
          SchedulerService.getTasks(d.idScheduleDay).then((r) => ({
            dayId: d.idScheduleDay,
            tasks: r.data,
          }))
        )
      );
      return Object.fromEntries(results.map((r) => [r.dayId, r.tasks]));
    },
    { enabled: !!days && days.length > 0, initialData: {} }
  );

  const upsertDayMutation = useMutation(
    (body) => SchedulerService.upsertDay(templateId, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["scheduler-days", templateId]);
        setDayDlg(null);
        showFeedback("success", "Day saved");
      },
      onError: (err) => {
        const msg = err?.response?.data?.error || "Failed to save day";
        showFeedback("error", msg);
      },
    }
  );

  const deleteDayMutation = useMutation(
    (day) => SchedulerService.deleteDay(templateId, day.idScheduleDay),
    {
      onSuccess: (_, day) => {
        queryClient.invalidateQueries(["scheduler-days", templateId]);
        queryClient.invalidateQueries(["scheduler-tasks-bulk"]);
        if (selectedDay?.idScheduleDay === day.idScheduleDay)
          setSelectedDay(null);
        setConfirmDeleteDay(null);
        showFeedback("success", `Day ${day.dayNumber} deleted`);
      },
      onError: () => showFeedback("error", "Failed to delete day"),
    }
  );

  const upsertTaskMutation = useMutation(
    ({ dayId, body }) => SchedulerService.upsertTask(dayId, body),
    {
      onSuccess: (_, { dayId }) => {
        queryClient.invalidateQueries(["scheduler-tasks-bulk"]);
        queryClient.invalidateQueries(["scheduler-tasks", dayId]);
        setTaskDlg(null);
        showFeedback("success", "Task saved");
      },
      onError: () => showFeedback("error", "Failed to save task"),
    }
  );

  const createNotifMutation = useMutation(
    (body) => NotificationTemplateService.create(body),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(["notification-templates-picker"]);
        setTaskForm((f) => ({ ...f, idProcess: res.data.idProcess }));
        setCreateNotifDlg(false);
        setNotifPickerOpen(false);
        showFeedback(
          "success",
          `Template "${res.data.idProcess}" created and selected`
        );
      },
      onError: () =>
        showFeedback("error", "Failed to create notification template"),
    }
  );

  const openNewDay = () => {
    setDayForm({ ...EMPTY_DAY });
    setDayDlg("new");
  };

  const openEditDay = (day) => {
    setDayForm({
      idScheduleDay: day.idScheduleDay,
      dayNumber: day.dayNumber,
      label: day.label || "",
      isEnabled: day.isEnabled,
    });
    setDayDlg(day);
  };

  const openNewTask = (dayId, taskType = "SMS") => {
    const defaultUtcHour = 9;
    const { hour: hourEastern } = getEasternTimeInfo(defaultUtcHour);
    setTaskForm({
      ...EMPTY_TASK,
      taskType,
      hourEastern,
    });
    setTaskDlg({ dayId });
  };

  const openEditTask = (dayId, task) => {
    const { hour: hourEastern } = getEasternTimeInfo(task.hourUtc);
    setTaskForm({
      idScheduleTask: task.idScheduleTask,
      taskType: task.taskType,
      hourUtc: task.hourUtc,
      hourEastern,
      subject: task.subject || "",
      idProcess: task.idProcess || "",
      bodyOverride: task.bodyOverride || "",
      conditionJson: task.conditionJson || "",
      sequenceOrder: task.sequenceOrder,
      status: task.status,
    });
    setTaskDlg({ dayId, task });
  };

  const openNotifPicker = () => {
    setNotifSearch("");
    setCreateNotifForm((f) => ({
      ...f,
      typeTemplate: TASK_TYPE_TO_TEMPLATE_TYPE[taskForm.taskType] || "SMS",
      idProcess: taskForm.idProcess || "",
    }));
    setNotifPickerOpen(true);
  };

  const sortedDays = days
    ? [...days].sort((a, b) => a.dayNumber - b.dayNumber)
    : [];

  const selectedDayTasks = selectedDay
    ? [...(tasksMap?.[selectedDay.idScheduleDay] || [])].sort(
        (a, b) => a.sequenceOrder - b.sequenceOrder
      )
    : [];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <IconButton onClick={goBack} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h6">
            {template?.name || "Template Detail"}
          </Typography>
          {template && (
            <Typography variant="body2" color="text.secondary">
              {template.scheduleType === "CUSTOMER_JOURNEY"
                ? "Customer Journey"
                : "Onboarding Campaign"}{" "}
              · {template.totalDays} days · v{template.version}
            </Typography>
          )}
        </Box>
        <RenderOnRole roles={["scheduler-admin", "pc-program-super-admin"]}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={openNewDay}
            size="small"
          >
            Add Day
          </Button>
        </RenderOnRole>
      </Box>

      {daysLoading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {!daysLoading && sortedDays.length === 0 && (
        <Alert severity="info">
          No days configured yet. Click <strong>Add Day</strong> to get started.
        </Alert>
      )}

      {/* ── Matrix Table ── */}
      {sortedDays.length > 0 && (
        <Paper variant="outlined">
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    Day
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Label</TableCell>
                  {CHANNELS.map((ch) => (
                    <TableCell
                      key={ch.type}
                      align="center"
                      sx={{
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        minWidth: 110,
                      }}
                    >
                      <Chip
                        label={ch.label}
                        size="small"
                        color={ch.color}
                        variant="outlined"
                        sx={{ fontWeight: 700 }}
                      />
                    </TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedDays.map((day) => {
                  const tasks = tasksMap?.[day.idScheduleDay] || [];
                  const isSelected =
                    selectedDay?.idScheduleDay === day.idScheduleDay;
                  return (
                    <TableRow
                      key={day.idScheduleDay}
                      hover
                      selected={isSelected}
                      sx={{ verticalAlign: "top" }}
                    >
                      {/* Day chip */}
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Chip
                          label={`Day ${day.dayNumber}`}
                          size="small"
                          color={day.isEnabled ? "success" : "default"}
                          variant="filled"
                          sx={{ minWidth: 58 }}
                        />
                      </TableCell>

                      {/* Label */}
                      <TableCell sx={{ minWidth: 140 }}>
                        <Typography variant="body2" noWrap>
                          {day.label || <em style={{ color: "#aaa" }}>—</em>}
                        </Typography>
                      </TableCell>

                      {/* One column per channel */}
                      {CHANNELS.map((ch) => {
                        const channelTasks = tasks
                          .filter((t) => t.taskType === ch.type)
                          .sort((a, b) => a.sequenceOrder - b.sequenceOrder);
                        return (
                          <TableCell key={ch.type} align="center">
                            <Box
                              display="flex"
                              flexWrap="wrap"
                              gap={0.5}
                              justifyContent="center"
                              alignItems="center"
                            >
                              {channelTasks.map((task) => (
                                <Chip
                                  key={task.idScheduleTask}
                                  label={`${getEasternTimeInfo(task.hourUtc).hour}:00 ${getEasternTimeInfo(task.hourUtc).timeZoneName}`}
                                  size="small"
                                  color={ch.color}
                                  variant={
                                    task.status === "ACTIVE"
                                      ? "filled"
                                      : "outlined"
                                  }
                                  onClick={() =>
                                    openEditTask(day.idScheduleDay, task)
                                  }
                                  clickable
                                  sx={{ fontSize: "0.7rem" }}
                                />
                              ))}
                              <RenderOnRole
                                roles={[
                                  "scheduler-admin",
                                  "pc-program-super-admin",
                                ]}
                              >
                                <Tooltip title={`Add ${ch.label} task`}>
                                  <Chip
                                    label="+"
                                    size="small"
                                    color={ch.color}
                                    variant="outlined"
                                    onClick={() =>
                                      openNewTask(day.idScheduleDay, ch.type)
                                    }
                                    clickable
                                    sx={{
                                      fontSize: "0.85rem",
                                      fontWeight: 700,
                                      borderStyle: "dashed",
                                      opacity: 0.65,
                                      "&:hover": { opacity: 1 },
                                      minWidth: 32,
                                      height: 22,
                                      "& .MuiChip-label": { px: 0.75 },
                                    }}
                                  />
                                </Tooltip>
                              </RenderOnRole>
                            </Box>
                          </TableCell>
                        );
                      })}

                      {/* Actions */}
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Tooltip title="Day detail">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setSelectedDay(isSelected ? null : day)
                            }
                            color={isSelected ? "primary" : "default"}
                          >
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <RenderOnRole
                          roles={["scheduler-admin", "pc-program-super-admin"]}
                        >
                          <Tooltip title="Edit day">
                            <IconButton
                              size="small"
                              onClick={() => openEditDay(day)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete day">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setConfirmDeleteDay(day)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </RenderOnRole>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Day Detail Drawer ── */}
      <Drawer
        anchor="right"
        open={!!selectedDay}
        onClose={() => setSelectedDay(null)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 480 }, p: 2 } }}
      >
        {selectedDay && (
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Chip
                label={`Day ${selectedDay.dayNumber}`}
                size="small"
                color={selectedDay.isEnabled ? "success" : "default"}
              />
              <Typography variant="h6" flex={1}>
                {selectedDay.label || "Day detail"}
              </Typography>
              <IconButton size="small" onClick={() => setSelectedDay(null)}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="subtitle2" color="text.secondary">
                {selectedDayTasks.length} task
                {selectedDayTasks.length !== 1 ? "s" : ""}
              </Typography>
              <RenderOnRole
                roles={["scheduler-admin", "pc-program-super-admin"]}
              >
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => openNewTask(selectedDay.idScheduleDay)}
                >
                  Add Task
                </Button>
              </RenderOnRole>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {selectedDayTasks.length === 0 ? (
              <Alert severity="info" variant="outlined">
                No tasks configured for this day.
              </Alert>
            ) : (
              <Box display="flex" flexDirection="column" gap={1.5}>
                {selectedDayTasks.map((task) => (
                  <Paper
                    key={task.idScheduleTask}
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: 2 }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Chip
                        size="small"
                        label={TASK_TYPE_LABELS[task.taskType] || task.taskType}
                        color={TASK_TYPE_COLORS[task.taskType] || "default"}
                        variant="filled"
                      />
                      <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                        {task.idProcess}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${getEasternTimeInfo(task.hourUtc).hour}:00 ${getEasternTimeInfo(task.hourUtc).timeZoneName}`}
                        variant="outlined"
                      />
                      <RenderOnRole
                        roles={["scheduler-admin", "pc-program-super-admin"]}
                      >
                        <Tooltip title="Edit task">
                          <IconButton
                            size="small"
                            onClick={() =>
                              openEditTask(selectedDay.idScheduleDay, task)
                            }
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </RenderOnRole>
                    </Box>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      <Chip
                        size="small"
                        label={`Order ${task.sequenceOrder}`}
                        variant="outlined"
                        sx={{ fontSize: "0.7rem" }}
                      />
                      <Chip
                        size="small"
                        label={task.status}
                        color={task.status === "ACTIVE" ? "success" : "default"}
                        variant="outlined"
                        sx={{ fontSize: "0.7rem" }}
                      />
                      {task.conditionJson && (
                        <Chip
                          size="small"
                          label="Conditional"
                          color="warning"
                          variant="outlined"
                          sx={{ fontSize: "0.7rem" }}
                        />
                      )}
                    </Box>
                    {task.subject && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mt={0.5}
                      >
                        Subject: {task.subject}
                      </Typography>
                    )}
                    {task.bodyOverride && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mt={0.25}
                      >
                        Override:{" "}
                        {task.bodyOverride.length > 80
                          ? task.bodyOverride.substring(0, 80) + "…"
                          : task.bodyOverride}
                      </Typography>
                    )}
                    {task.conditionJson && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mt={0.25}
                        sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                      >
                        {task.conditionJson}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Drawer>

      {/* ── Confirm delete day dialog ─── */}
      <Dialog
        open={!!confirmDeleteDay}
        onClose={() => setConfirmDeleteDay(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Delete Day {confirmDeleteDay?.dayNumber}?
          <IconButton
            onClick={() => setConfirmDeleteDay(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={2}>
            This will permanently delete{" "}
            <strong>Day {confirmDeleteDay?.dayNumber}</strong>
            {confirmDeleteDay?.label ? ` — ${confirmDeleteDay.label}` : ""} and
            all its tasks. This action cannot be undone.
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button onClick={() => setConfirmDeleteDay(null)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              disabled={deleteDayMutation.isLoading}
              onClick={() => deleteDayMutation.mutate(confirmDeleteDay)}
            >
              Delete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Day dialog ─── */}
      <Dialog
        open={!!dayDlg}
        onClose={() => setDayDlg(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {dayDlg === "new" ? "Add Day" : "Edit Day"}
          <IconButton
            onClick={() => setDayDlg(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={6}>
              <TextField
                label="Day Number"
                type="number"
                inputProps={{ min: 1, max: 365 }}
                value={dayForm.dayNumber}
                onChange={(e) =>
                  setDayForm((f) => ({
                    ...f,
                    dayNumber: parseInt(e.target.value, 10) || 1,
                  }))
                }
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6} display="flex" alignItems="center" gap={1}>
              <Typography variant="body2">Enabled</Typography>
              <Switch
                checked={dayForm.isEnabled}
                onChange={(e) =>
                  setDayForm((f) => ({ ...f, isEnabled: e.target.checked }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Label (optional)"
                value={dayForm.label}
                onChange={(e) =>
                  setDayForm((f) => ({ ...f, label: e.target.value }))
                }
                fullWidth
              />
            </Grid>
          </Grid>
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={() => upsertDayMutation.mutate(dayForm)}
              disabled={upsertDayMutation.isLoading}
            >
              Save Day
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Task dialog ─── */}
      <Dialog
        open={!!taskDlg}
        onClose={() => setTaskDlg(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {taskDlg?.task ? "Edit Task" : "Add Task"}
          <IconButton
            onClick={() => setTaskDlg(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Channel Type</InputLabel>
                <Select
                  value={taskForm.taskType}
                  label="Channel Type"
                  onChange={(e) =>
                    setTaskForm((f) => ({ ...f, taskType: e.target.value }))
                  }
                >
                  <MenuItem value="SMS">SMS</MenuItem>
                  <MenuItem value="EMAIL">Email</MenuItem>
                  <MenuItem value="VOICE">Voice Call</MenuItem>
                  <MenuItem value="APP_MESSAGE">App Message</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Hour (Eastern Time)"
                type="number"
                inputProps={{ min: 0, max: 23 }}
                value={taskForm.hourEastern ?? ""}
                onChange={(e) => {
                  const easternHour = parseInt(e.target.value, 10);
                  setTaskForm((f) => ({
                    ...f,
                    hourEastern: easternHour,
                    ...(Number.isFinite(easternHour)
                      ? { hourUtc: easternToUtcHour(easternHour) }
                      : {}),
                  }));
                }}
                helperText={`UTC: ${taskForm.hourUtc} (${getEasternTimeInfo(taskForm.hourUtc).timeZoneName})`}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Order"
                type="number"
                inputProps={{ min: 1 }}
                value={taskForm.sequenceOrder}
                onChange={(e) =>
                  setTaskForm((f) => ({
                    ...f,
                    sequenceOrder: parseInt(e.target.value, 10) || 1,
                  }))
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" gap={1} alignItems="flex-start">
                <TextField
                  label="Process ID"
                  value={taskForm.idProcess}
                  onChange={(e) =>
                    setTaskForm((f) => ({ ...f, idProcess: e.target.value }))
                  }
                  fullWidth
                  required
                  helperText="Notification template key (e.g. WELCOME_DAY1_SMS)"
                />
                <Tooltip title="Pick from notification templates">
                  <IconButton onClick={openNotifPicker} sx={{ mt: 0.5 }}>
                    <ListAltIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            {taskForm.taskType === "EMAIL" && (
              <Grid item xs={12}>
                <TextField
                  label="Email Subject"
                  value={taskForm.subject}
                  onChange={(e) =>
                    setTaskForm((f) => ({ ...f, subject: e.target.value }))
                  }
                  fullWidth
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label="Body Override (optional)"
                value={taskForm.bodyOverride}
                onChange={(e) =>
                  setTaskForm((f) => ({ ...f, bodyOverride: e.target.value }))
                }
                fullWidth
                multiline
                rows={3}
                helperText="Leave blank to use the notification template body"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Condition JSON (optional)"
                value={taskForm.conditionJson}
                onChange={(e) =>
                  setTaskForm((f) => ({ ...f, conditionJson: e.target.value }))
                }
                fullWidth
                multiline
                rows={2}
                helperText='e.g. {"attribute":"PAD_USAGE_SEGMENT","operator":"IN","values":["MID_USAGE","HIGH_USAGE"]}'
              />
            </Grid>
            {taskDlg?.task && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={taskForm.status}
                    label="Status"
                    onChange={(e) =>
                      setTaskForm((f) => ({ ...f, status: e.target.value }))
                    }
                  >
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="INACTIVE">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={() =>
                upsertTaskMutation.mutate({
                  dayId: taskDlg.dayId,
                  body: taskForm,
                })
              }
              disabled={!taskForm.idProcess || upsertTaskMutation.isLoading}
            >
              Save Task
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Notification Template Picker dialog ─── */}
      <Dialog
        open={notifPickerOpen}
        onClose={() => setNotifPickerOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Select Notification Template
          <IconButton
            onClick={() => setNotifPickerOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1.5}
            gap={1}
          >
            <TextField
              size="small"
              placeholder="Search by process ID or notification type…"
              value={notifSearch}
              onChange={(e) => setNotifSearch(e.target.value)}
              sx={{ flex: 1 }}
              autoFocus
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                setCreateNotifForm((f) => ({
                  ...f,
                  typeTemplate:
                    TASK_TYPE_TO_TEMPLATE_TYPE[taskForm.taskType] || "SMS",
                  idProcess: taskForm.idProcess || "",
                }));
                setCreateNotifDlg(true);
              }}
            >
              Create New
            </Button>
          </Box>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: 420 }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Process ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    Notification Type
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Lang</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(notifTemplates || [])
                  .filter(
                    (t) =>
                      t.typeTemplate ===
                        TASK_TYPE_TO_TEMPLATE_TYPE[taskForm.taskType] &&
                      (!notifSearch ||
                        t.idProcess
                          .toLowerCase()
                          .includes(notifSearch.toLowerCase()) ||
                        (t.notificationTypeId || "")
                          .toLowerCase()
                          .includes(notifSearch.toLowerCase()))
                  )
                  .map((t) => {
                    const showPreview =
                      (taskForm.taskType === "SMS" ||
                        taskForm.taskType === "APP_MESSAGE") &&
                      !!t.template;
                    return (
                      <Tooltip
                        key={t.idNotificationTemplate}
                        title={
                          showPreview ? (
                            <Typography
                              variant="caption"
                              sx={{
                                whiteSpace: "pre-wrap",
                                maxWidth: 360,
                                display: "block",
                              }}
                            >
                              {t.template}
                            </Typography>
                          ) : (
                            ""
                          )
                        }
                        placement="left"
                        arrow
                        disableHoverListener={!showPreview}
                      >
                        <TableRow
                          hover
                          selected={t.idProcess === taskForm.idProcess}
                          onClick={() => {
                            setTaskForm((f) => ({
                              ...f,
                              idProcess: t.idProcess,
                            }));
                            setNotifPickerOpen(false);
                          }}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>{t.idProcess}</TableCell>
                          <TableCell>{t.notificationTypeId || "—"}</TableCell>
                          <TableCell>{t.lang || "—"}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={t.status}
                              color={
                                t.status === "ACTIVE" ? "success" : "default"
                              }
                              variant="outlined"
                              sx={{ fontSize: "0.7rem" }}
                            />
                          </TableCell>
                        </TableRow>
                      </Tooltip>
                    );
                  })}
                {notifTemplates &&
                  notifTemplates.filter(
                    (t) =>
                      t.typeTemplate ===
                        TASK_TYPE_TO_TEMPLATE_TYPE[taskForm.taskType] &&
                      (!notifSearch ||
                        t.idProcess
                          .toLowerCase()
                          .includes(notifSearch.toLowerCase()) ||
                        (t.notificationTypeId || "")
                          .toLowerCase()
                          .includes(notifSearch.toLowerCase()))
                  ).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          py={2}
                        >
                          {notifSearch
                            ? `No results for "${notifSearch}"`
                            : `No ${TASK_TYPE_TO_TEMPLATE_TYPE[taskForm.taskType]} templates found.`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>

      {/* ── Create Notification Template sub-dialog ─── */}
      <Dialog
        open={createNotifDlg}
        onClose={() => setCreateNotifDlg(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          New Notification Template
          <IconButton
            onClick={() => setCreateNotifDlg(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12}>
              <TextField
                label="Process ID"
                value={createNotifForm.idProcess}
                onChange={(e) =>
                  setCreateNotifForm((f) => ({
                    ...f,
                    idProcess: e.target.value,
                  }))
                }
                fullWidth
                required
                helperText="Unique key identifying this notification (e.g. WELCOME_DAY1_SMS)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Channel Type</InputLabel>
                <Select
                  value={createNotifForm.typeTemplate}
                  label="Channel Type"
                  onChange={(e) =>
                    setCreateNotifForm((f) => ({
                      ...f,
                      typeTemplate: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="SMS">SMS</MenuItem>
                  <MenuItem value="EMAIL">Email</MenuItem>
                  <MenuItem value="VOICE">Voice</MenuItem>
                  <MenuItem value="APP">App Message</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Language"
                value={createNotifForm.lang}
                onChange={(e) =>
                  setCreateNotifForm((f) => ({ ...f, lang: e.target.value }))
                }
                fullWidth
                inputProps={{ maxLength: 2 }}
                placeholder="en"
                helperText="2-letter code"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={createNotifForm.status}
                  label="Status"
                  onChange={(e) =>
                    setCreateNotifForm((f) => ({
                      ...f,
                      status: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {createNotifForm.typeTemplate === "EMAIL" && (
              <Grid item xs={12}>
                <TextField
                  label="SendGrid Template ID"
                  value={createNotifForm.idTemplate}
                  onChange={(e) =>
                    setCreateNotifForm((f) => ({
                      ...f,
                      idTemplate: e.target.value,
                    }))
                  }
                  fullWidth
                  placeholder="d-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  helperText="Dynamic template ID from SendGrid"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label="Message Body"
                value={createNotifForm.template}
                onChange={(e) =>
                  setCreateNotifForm((f) => ({
                    ...f,
                    template: e.target.value,
                  }))
                }
                fullWidth
                multiline
                rows={4}
                helperText="Use {{TOKEN_NAME}} for dynamic values. Leave blank if using an external template ID."
              />
            </Grid>
          </Grid>
          <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
            <Button onClick={() => setCreateNotifDlg(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={
                !createNotifForm.idProcess ||
                !createNotifForm.typeTemplate ||
                createNotifMutation.isLoading
              }
              onClick={() => createNotifMutation.mutate(createNotifForm)}
            >
              {createNotifMutation.isLoading ? "Creating…" : "Create & Select"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Feedback */}
      <Snackbar
        open={feedback.open}
        autoHideDuration={4000}
        onClose={() => setFeedback((f) => ({ ...f, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={feedback.type}
          onClose={() => setFeedback((f) => ({ ...f, open: false }))}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
